import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import PhoneInput from 'react-phone-input-2'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Button } from 'components/ui/button'
import { updateUserInfo, verifyOtp } from 'store/actions/auth.action'
import { notifyToast } from 'utils/responseUtils'
import { supabase } from 'config/supabase'
import Link from 'next/link'
import Image from 'next/image'

const PhoneVerifyModal = ({ onClose }) => {
  const dispatch = useDispatch()
  const [showOTP, setShowOTP] = useState(false)
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [timer, setTimer] = useState(60)

  useEffect(() => {
    const loadCSS = async () => {
      await import('react-phone-input-2/lib/style.css')
    }
    loadCSS()
  }, [])

  useEffect(() => {
    let interval
    if (timer > 0 && showOTP) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [timer, showOTP])

  const handleSendOtp = async (type) => {
    if (phone.length) {
      if (type !== 'resend') setShowOTP(true)
      else setTimer(60)
      const { error } = await supabase.auth.updateUser({
        phone: `+${phone}`,
      })
      if (error) {
        console.error('Error sending verification code:', error.message)
        return false
      }
      dispatch(notifyToast('success', 'Verification Code Sent to your Phone'))
    } else {
      dispatch(notifyToast('error', 'Please enter a valid Phone number'))
    }
  }

  const handleVerifyOTP = async () => {
    const { error } = await verifyOtp(phone, otp)
    if (error) return dispatch(notifyToast("It seems you've entered the Incorrect OTP"))
    dispatch(updateUserInfo({ phone }))
    return onClose()
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-6 rounded-lg bg-white shadow-xl">
        <DialogHeader>
          <DialogTitle className="flex justify-center items-center text-2xl font-bold tracking-wider">
            <Image
              src="/assets/common/StyleyLogo.png"
              alt="Styley Logo"
              width={90}
              height={24}
              className="object-contain h-6 w-auto"
              priority
            />
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6 items-center">
          {!showOTP ? (
            <div className="w-full">
              <PhoneInput
                inputClass="!w-full !bg-gray-100 !text-black !border-0 !h-12 rounded-md"
                buttonClass="!bg-gray-100 !border-0"
                enableSearch={false}
                country={'us'}
                className="!w-full bg-gray-100 h-12"
                onChange={(value) => setPhone(value)}
                placeholder="Enter Phone Number"
                value={phone}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4 w-full">
              <Input
                placeholder="Input Verification Code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full h-12 border-gray-300 focus:ring-2 focus:ring-black"
              />
              <Button
                onClick={handleVerifyOTP}
                className="w-full h-12 text-white bg-black rounded-md transition-colors hover:bg-gray-800"
              >
                Enter
              </Button>
              <div className="flex justify-between items-center text-sm">
                <Button
                  variant="link"
                  className={`text-black font-semibold ${timer ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={timer > 0}
                  onClick={() => handleSendOtp('resend')}
                >
                  Resend OTP
                </Button>
                {showOTP && (
                  <p className="font-semibold text-black">
                    {`${String(Math.floor(timer / 60)).padStart(2, '0')}:${String(timer % 60).padStart(2, '0')} min`}
                  </p>
                )}
              </div>
              <p className="text-xs text-center text-gray-500">
                By entering your phone number, you authorize us to send you SMS
              </p>
            </div>
          )}

          {!showOTP && (
            <Button
              onClick={() => handleSendOtp()}
              className="w-full h-12 text-white bg-black rounded-md transition-colors hover:bg-gray-800"
            >
              Send Verification Code
            </Button>
          )}

          <p className="text-sm text-center text-gray-500">
            By providing my contact information, I acknowledge and give my explicit consent to be contacted via SMS.
            Message and data rates may apply. Message frequency may vary. Reply STOP to opt out.
          </p>

          <Button variant="ghost" onClick={onClose}>
            Do Later
          </Button>

          <Link href="/terms-and-conditions" target="_blank">
            <span className="text-base font-semibold text-gray-600 underline transition-colors hover:text-black">
              Terms of Use and Privacy Policy
            </span>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PhoneVerifyModal
